import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { ChevronRight, X, Plus, Trash, Pencil, FolderPlus } from 'lucide-react';
import { useAuthStore, useFeedsStore, useArticlesStore, useFoldersStore } from '../store/useStore';
import { feeds as feedsApi, folders as foldersApi } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import toast from 'react-hot-toast';

interface MobileFeedDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const FOLDER_GLYPHS = ['▲', '■', '●', '◆'];
const UNFILED_KEY = '__unfiled__';

export default function MobileFeedDrawer({ isOpen, onClose }: MobileFeedDrawerProps) {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const feeds = useFeedsStore((state) => state.feeds);
  const selectedFeedId = useFeedsStore((state) => state.selectedFeedId);
  const selectedFolderId = useFeedsStore((state) => state.selectedFolderId);
  const setSelectedFeedId = useFeedsStore((state) => state.setSelectedFeedId);
  const setSelectedFolderId = useFeedsStore((state) => state.setSelectedFolderId);
  const setFilter = useArticlesStore((state) => state.setFilter);
  const setBucket = useArticlesStore((state) => state.setBucket);
  const folders = useFoldersStore((state) => state.folders);
  const setFolders = useFoldersStore((state) => state.setFolders);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [showAddFeed, setShowAddFeed] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [detectedFeeds, setDetectedFeeds] = useState<Array<{ url: string; title: string; description?: string }>>([]);
  const [detectingFeeds, setDetectingFeeds] = useState(false);
  const [selectedFeedUrl, setSelectedFeedUrl] = useState('');
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState<string | null>(null);
  const addFolder = useFoldersStore((state) => state.addFolder);
  const updateFolder = useFoldersStore((state) => state.updateFolder);
  const removeFolder = useFoldersStore((state) => state.removeFolder);

  useEffect(() => {
    async function loadFolders() {
      try {
        const foldersData = await foldersApi.getAll(token);
        setFolders(foldersData);
      } catch (error) {
        console.error('Failed to load folders:', error);
      }
    }

    loadFolders();
  }, [token, setFolders]);

  // Open the folder that holds the current selection
  useEffect(() => {
    if (selectedFolderId) {
      setExpandedFolders((prev) => new Set(prev).add(selectedFolderId));
      return;
    }
    if (!selectedFeedId) return;
    const parent = folders.find((folder) =>
      folder.subscriptions?.some((sub: any) => sub.feed?.id === selectedFeedId)
    );
    setExpandedFolders((prev) => new Set(prev).add(parent ? parent.id : UNFILED_KEY));
  }, [selectedFeedId, selectedFolderId, folders]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => onClose(),
    trackMouse: false,
  });

  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  const feedsInFolders = new Set<string>();
  folders.forEach(folder => {
    folder.subscriptions?.forEach((sub: any) => feedsInFolders.add(sub.id));
  });
  const unfiledFeeds = feeds.filter(feed => !feedsInFolders.has(feed.id));

  function toggleFolder(folderId: string) {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }

  function openRead() {
    setFilter('unread');
    setBucket('today');
    navigate('/read');
    onClose();
  }

  function handleFeedClick(feedId: string) {
    setSelectedFeedId(feedId);
    setSelectedFolderId(null);
    openRead();
  }

  function handleFolderClick(folderId: string) {
    setExpandedFolders(prev => new Set(prev).add(folderId));
    setSelectedFolderId(folderId);
    setSelectedFeedId(null);
    openRead();
  }

  function handleAllClick() {
    setSelectedFeedId(null);
    setSelectedFolderId(null);
    openRead();
  }

  async function handleDetectFeeds() {
    if (!feedUrl.trim()) return;

    setDetectingFeeds(true);
    setDetectedFeeds([]);

    try {
      const result = await feedsApi.detect(token, feedUrl.trim());
      setDetectedFeeds(result.feeds);

      if (result.feeds.length === 0) {
        toast.error('No RSS feeds found at this URL');
      } else if (result.feeds.length === 1) {
        setSelectedFeedUrl(result.feeds[0].url);
      }
    } catch {
      toast.error('Failed to detect feeds');
      setDetectedFeeds([]);
    } finally {
      setDetectingFeeds(false);
    }
  }

  async function handleAddFeed(e: React.FormEvent) {
    e.preventDefault();
    setSubscribing(true);

    const urlToSubscribe = selectedFeedUrl || feedUrl;

    try {
      await feedsApi.subscribe(token, { url: urlToSubscribe });
      trackEvent('feed_subscribe');
      toast.success('Feed subscribed');
      setFeedUrl('');
      setSelectedFeedUrl('');
      setDetectedFeeds([]);
      setShowAddFeed(false);

      const feedsData = await feedsApi.getAll(token);
      useFeedsStore.getState().setFeeds(feedsData);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  }

  async function handleCreateFolder(e: React.FormEvent) {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    setCreatingFolder(true);
    try {
      const folder = await foldersApi.create(token, { name: newFolderName.trim() });
      addFolder(folder);
      setNewFolderName('');
      setShowCreateFolder(false);
      toast.success('Folder created');
    } catch {
      toast.error('Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  }

  async function handleEditFolder(folderId: string, newName: string) {
    try {
      const updatedFolder = await foldersApi.update(token, folderId, { name: newName });
      updateFolder(folderId, updatedFolder);
      setEditingFolder(null);
      setEditingFolderName('');
      toast.success('Folder renamed');
    } catch {
      toast.error('Failed to rename folder');
    }
  }

  async function handleDeleteFolder(folderId: string) {
    try {
      await foldersApi.delete(token, folderId);
      removeFolder(folderId);
      setShowDeleteConfirm(null);
      toast.success('Folder deleted');
      const feedsData = await feedsApi.getAll(token);
      useFeedsStore.getState().setFeeds(feedsData);
    } catch {
      toast.error('Failed to delete folder');
    }
  }

  async function handleUnsubscribe(subscriptionId: string) {
    try {
      await feedsApi.unsubscribe(token, subscriptionId);
      toast.success('Unsubscribed');
      setShowUnsubscribeConfirm(null);
      const feedsData = await feedsApi.getAll(token);
      useFeedsStore.getState().setFeeds(feedsData);
    } catch {
      toast.error('Failed to unsubscribe');
    }
  }

  if (!isOpen) return null;

  function FeedRow({ subscription }: { subscription: any }) {
    const isSelected = selectedFeedId === subscription.feed.id;
    const hasUnread = (subscription.unreadCount ?? 0) > 0;
    return (
      <div>
        <div className={`flex items-start border-b border-edge-subtle ${
          isSelected ? 'bg-ink text-canvas' : 'text-ink-secondary'
        }`}>
          <button
            onClick={() => handleFeedClick(subscription.feed.id)}
            className="flex-1 min-w-0 text-left flex items-start gap-2 pl-8 pr-2 py-2.5"
          >
            <span className={`stamp mt-[3px] ${isSelected ? 'text-canvas' : ''} ${hasUnread ? '' : 'opacity-0'}`} aria-label="unread">{subscription.unreadCount ?? 0}</span>
            <span className="text-[14px] leading-snug line-clamp-2">{subscription.title || subscription.feed.title}</span>
          </button>
          <button
            onClick={() => setShowUnsubscribeConfirm(subscription.id)}
            className={`p-2.5 ${isSelected ? 'text-canvas' : 'text-ink-faint'}`}
            aria-label="Unsubscribe"
          >
            <Trash className="w-4 h-4" />
          </button>
        </div>
        {showUnsubscribeConfirm === subscription.id && (
          <div className="p-3 border-b border-edge-subtle">
            <p className="text-sm text-ink-secondary mb-2">Unsubscribe from "{subscription.feed.title}"?</p>
            <div className="flex gap-2">
              <button onClick={() => handleUnsubscribe(subscription.id)} className="btn btn-danger flex-1">
                Unsubscribe
              </button>
              <button onClick={() => setShowUnsubscribeConfirm(null)} className="btn btn-quiet">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className="lg:hidden fixed inset-0 bg-black/50 z-40"
        onClick={handleBackdropClick}
      />

      <div
        {...swipeHandlers}
        className="lg:hidden fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-canvas border-r border-edge z-50 flex flex-col"
      >
        <div className="flex items-center justify-between h-12 pl-4 border-b border-edge">
          <span className="kicker text-ink">Feeds</span>
          <div className="flex items-stretch h-full">
            <button
              onClick={() => { setShowCreateFolder(!showCreateFolder); setShowAddFeed(false); }}
              className="px-3 border-l border-edge text-ink-secondary"
              aria-label="New folder"
            >
              <FolderPlus className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setShowAddFeed(!showAddFeed); setShowCreateFolder(false); }}
              className="px-3 border-l border-edge text-ink-secondary"
              aria-label="Add feed"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="px-3 border-l border-edge text-ink-secondary"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pb-20">
          {showCreateFolder && (
            <form onSubmit={handleCreateFolder} className="p-3 border-b border-edge space-y-2">
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                className="field"
                maxLength={100}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={creatingFolder || !newFolderName.trim()}
                  className="btn btn-ink flex-1"
                >
                  {creatingFolder ? 'Creating…' : 'Create folder'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }}
                  className="btn btn-quiet"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {showAddFeed && (
            <div className="p-3 border-b border-edge space-y-2">
              <div className="flex gap-2">
                <input
                  type="url"
                  value={feedUrl}
                  onChange={(e) => {
                    setFeedUrl(e.target.value);
                    setDetectedFeeds([]);
                    setSelectedFeedUrl('');
                  }}
                  placeholder="Site or feed URL"
                  className="field"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleDetectFeeds}
                  disabled={!feedUrl.trim() || detectingFeeds}
                  className="btn"
                >
                  {detectingFeeds ? '…' : 'Find'}
                </button>
              </div>

              {detectedFeeds.length > 0 && (
                <div className="border border-edge-subtle">
                  {detectedFeeds.map((feed) => (
                    <label key={feed.url} className="flex items-start gap-2 p-2 border-b border-edge-subtle last:border-b-0 cursor-pointer">
                      <input
                        type="radio"
                        name="selectedFeed"
                        value={feed.url}
                        checked={selectedFeedUrl === feed.url}
                        onChange={(e) => setSelectedFeedUrl(e.target.value)}
                        className="mt-1 accent-[var(--ink)]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-ink line-clamp-2">{feed.title}</div>
                        <div className="kicker normal-case tracking-normal truncate">{feed.url}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              <form onSubmit={handleAddFeed} className="flex gap-2">
                <button
                  type="submit"
                  disabled={subscribing || (!selectedFeedUrl && !feedUrl.trim())}
                  className="btn btn-ink flex-1"
                >
                  {subscribing ? 'Adding…' : 'Add feed'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddFeed(false);
                    setFeedUrl('');
                    setSelectedFeedUrl('');
                    setDetectedFeeds([]);
                  }}
                  className="btn btn-quiet"
                >
                  Cancel
                </button>
              </form>
            </div>
          )}

          <button
            onClick={handleAllClick}
            className={`w-full text-left px-4 py-2.5 text-sm font-semibold border-b border-edge-subtle ${
              !selectedFeedId && !selectedFolderId ? 'bg-ink text-canvas' : 'text-ink'
            }`}
          >
            All feeds
          </button>

          {folders.map((folder, index) => {
            const expanded = expandedFolders.has(folder.id);
            const isSelected = selectedFolderId === folder.id;
            const hasUnread = (folder.unreadCount ?? 0) > 0;
            return (
              <div key={folder.id}>
                <div className={`flex items-start border-b border-edge-subtle ${isSelected ? 'bg-ink text-canvas' : 'text-ink'}`}>
                  <button
                    onClick={() => toggleFolder(folder.id)}
                    className={`pl-3 pr-1 py-3 ${isSelected ? 'text-canvas' : 'text-ink-muted'}`}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                  >
                    <ChevronRight className={`w-4 h-4 transition-transform duration-100 ${expanded ? 'rotate-90' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleFolderClick(folder.id)}
                    className="flex-1 min-w-0 flex items-start gap-2 pr-2 py-2.5 text-left"
                  >
                    <span className={`font-mono text-[10px] mt-[4px] ${isSelected ? 'text-canvas' : 'text-ink-muted'}`} aria-hidden>
                      {FOLDER_GLYPHS[index % FOLDER_GLYPHS.length]}
                    </span>
                    <span className="text-[14px] font-semibold leading-snug line-clamp-2 flex-1">{folder.name}</span>
                    {hasUnread && !expanded && <span className={`stamp mt-[3px] ${isSelected ? 'text-canvas' : ''}`}>{folder.unreadCount}</span>}
                  </button>
                  <button
                    onClick={() => { setEditingFolder(folder.id); setEditingFolderName(folder.name); }}
                    className={`p-2.5 ${isSelected ? 'text-canvas' : 'text-ink-faint'}`}
                    aria-label="Rename folder"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(folder.id)}
                    className={`p-2.5 ${isSelected ? 'text-canvas' : 'text-ink-faint'}`}
                    aria-label="Delete folder"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </div>

                {editingFolder === folder.id && (
                  <form
                    onSubmit={(e) => { e.preventDefault(); handleEditFolder(folder.id, editingFolderName); }}
                    className="p-3 border-b border-edge-subtle space-y-2"
                  >
                    <input
                      type="text"
                      value={editingFolderName}
                      onChange={(e) => setEditingFolderName(e.target.value)}
                      className="field"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button type="submit" className="btn btn-ink flex-1">Save</button>
                      <button
                        type="button"
                        onClick={() => { setEditingFolder(null); setEditingFolderName(''); }}
                        className="btn btn-quiet"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {showDeleteConfirm === folder.id && (
                  <div className="p-3 border-b border-edge-subtle">
                    <p className="text-sm text-ink-secondary mb-2">
                      Delete "{folder.name}"? Its feeds move to Unfiled.
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => handleDeleteFolder(folder.id)} className="btn btn-danger flex-1">
                        Delete
                      </button>
                      <button onClick={() => setShowDeleteConfirm(null)} className="btn btn-quiet">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {expanded && folder.subscriptions.map((subscription: any) => (
                  <FeedRow key={subscription.id} subscription={subscription} />
                ))}
              </div>
            );
          })}

          <button
            onClick={() => toggleFolder(UNFILED_KEY)}
            className="w-full flex items-center gap-1 pl-3 pr-3 py-3 border-b border-edge-subtle text-left"
            aria-expanded={expandedFolders.has(UNFILED_KEY)}
          >
            <ChevronRight className={`w-4 h-4 text-ink-muted transition-transform duration-100 ${expandedFolders.has(UNFILED_KEY) ? 'rotate-90' : ''}`} />
            <span className="kicker flex-1">Unfiled · {unfiledFeeds.length}</span>
            {!expandedFolders.has(UNFILED_KEY) && unfiledFeeds.some((f) => (f.unreadCount ?? 0) > 0) && (
              <span className="stamp">{unfiledFeeds.reduce((n, f) => n + (f.unreadCount ?? 0), 0)}</span>
            )}
          </button>
          {expandedFolders.has(UNFILED_KEY) && unfiledFeeds.map((subscription) => (
            <FeedRow key={subscription.id} subscription={subscription} />
          ))}

          {feeds.length === 0 && (
            <div className="px-4 py-8">
              <div className="kicker mb-1">Empty</div>
              <p className="text-sm text-ink-secondary">No feeds yet. Add one with the + above.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
